<template>
  <div class="flex flex-col md:flex-row gap-4">
    <!-- 지도 영역 -->
    <div ref="mapContainer" :class="'w-full ' + `${isDetailPage ? 'md:w-full' : 'md:w-2/3'}` + ' h-[300px] rounded-xl shadow-lg border border-gray-200 relative'"></div>

    <!-- 검색 및 결과 목록 -->
    <div v-show="!isDetailPage" class="w-full md:w-1/3 flex flex-col gap-2 h-[300px]">
      <!-- 검색 폼 -->
      <div class="flex gap-2">
        <input
            v-model="query"
            @keyup.enter="searchPlace"
            type="text"
            placeholder="장소 검색"
            class="form-input md:w-3/4"
        />
        <button
            @click="searchPlace"
            class="btn btn-primary btn-md md:w-1/4"
        >
          검색
        </button>
      </div>

      <!-- 검색 결과 / 안내 메시지 -->
      <div class="flex-1 overflow-y-auto">
        <div v-if="places.length">
          <div
              v-for="(place, index) in places"
              :key="place.id || index"
              @click="!isDetailPage && selectPlace(place)"
              class="p-3 mb-2 rounded-lg shadow-sm cursor-pointer transition hover:shadow-md hover:bg-blue-50 border border-gray-100"
              :class="{'bg-blue-100 font-semibold border-blue-200': selectedPlace?.id === place.id}"
          >
            <p class="text-blue-800 font-medium">{{ place.place_name }}</p>
            <p class="text-blue-600 text-sm">{{ place.road_address_name || place.address_name }}</p>
          </div>
        </div>
        <div v-else class="text-gray-400 text-center mt-4">
          검색 결과가 없습니다. 장소명을 입력하고 검색하세요.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, defineEmits, watch } from "vue";
import { loadKakaoMap } from "@/utils/kakaoMapLoader";

interface Props {
  placeName?: string;
  address?: string;
  isDetailPage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeName: "",
  address: "",
  isDetailPage: false,
});

const emit = defineEmits<{
  (e: "select", place: any): void;
}>();

const mapContainer = ref<HTMLDivElement | null>(null);
const map = ref<any>(null);
const query = ref("");
const places = ref<any[]>([]);
const selectedPlace = ref<any>(null);
let markers: any[] = [];
let overlay: any = null;

// 심플 현대 마커 생성 함수
const createMarker = (place: any) => {
  const svg = `
  <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#1d4ed8" stroke="white" stroke-width="2"/>
  </svg>
  `;
  const markerImage = new window.kakao.maps.MarkerImage(
      "data:image/svg+xml;base64," + btoa(svg),
      new window.kakao.maps.Size(24, 24)
  );

  const marker = new window.kakao.maps.Marker({
    map: map.value,
    position: new window.kakao.maps.LatLng(place.y, place.x),
    image: markerImage,
  });

  window.kakao.maps.event.addListener(marker, "click", () => {
    selectPlace(place);
  });

  return marker;
};

// 지도 초기화 및 Kakao Maps 로딩
onMounted(async () => {
  await loadKakaoMap(); // Kakao Maps API 로드
  query.value = props.placeName;

  const options = {
    center: new window.kakao.maps.LatLng(37.5665, 126.978),
    level: 5,
  };

  map.value = new window.kakao.maps.Map(mapContainer.value, options);

  const ps = new window.kakao.maps.services.Places();
  ps.keywordSearch(query.value, (data: any[], status: string) => {
    if (status === window.kakao.maps.services.Status.OK && data) {
      const place = data.find(
          (place: any) =>
              place.place_name === props.placeName &&
              (place.road_address_name === props.address || place.address_name === props.address)
      );
      if (place) selectPlace(place);
    }
  });
});

// 기존 마커 제거
const clearMarkers = () => {
  markers.forEach((marker) => marker.setMap(null));
  markers = [];
  if (overlay) {
    overlay.setMap(null);
    overlay = null;
  }
};

// 장소 검색
const searchPlace = () => {
  if (!query.value || !map.value) return;

  const ps = new window.kakao.maps.services.Places();
  ps.keywordSearch(query.value, (data: any[], status: string) => {
    if (status === window.kakao.maps.services.Status.OK) {
      places.value = data;
      clearMarkers();

      // 지도 중심 이동
      const first = data[0];
      map.value.setCenter(new window.kakao.maps.LatLng(first.y, first.x));

      // 마커 생성
      markers = data.map(createMarker);
    } else {
      places.value = [];
      clearMarkers();
    }
  });
};

// 선택 마커 강조 + 오버레이 표시
const selectPlace = (place: any) => {
  selectedPlace.value = place;
  emit("select", place);

  // 지도 중심 이동
  map.value.setCenter(new window.kakao.maps.LatLng(place.y, place.x));

  // 마커 강조
  highlightMarker(place);

  // 커스텀 오버레이 표시
  showOverlay(place);
};

// 마커 강조: SVG 색상 변경
const highlightMarker = (place: any) => {
  markers.forEach((m) => {
    const pos = m.getPosition();
    if (pos.getLat() === place.y && pos.getLng() === place.x) {
      const svg = `
      <svg width="30" height="30" xmlns="http://www.w3.org/2000/svg">
        <circle cx="15" cy="15" r="12" fill="#2563eb" stroke="white" stroke-width="2"/>
      </svg>`;
      m.setImage(
          new window.kakao.maps.MarkerImage(
              "data:image/svg+xml;base64," + btoa(svg),
              new window.kakao.maps.Size(30, 30)
          )
      );
    } else {
      // 기본 마커
      const svg = `
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#1d4ed8" stroke="white" stroke-width="2"/>
      </svg>`;
      m.setImage(
          new window.kakao.maps.MarkerImage(
              "data:image/svg+xml;base64," + btoa(svg),
              new window.kakao.maps.Size(24, 24)
          )
      );
    }
  });
};

// 지도 위 커스텀 오버레이 생성 (한 번만)
const createOverlay = () => {
  const content = document.createElement("div");
  content.className = "bg-white p-2 rounded-lg shadow-md border border-blue-200 text-blue-800 text-sm";
  overlay = new window.kakao.maps.CustomOverlay({
    content,
    map: map.value,
    position: new window.kakao.maps.LatLng(0, 0),
    yAnchor: 1,
  });
};

// 선택 장소 표시
const showOverlay = (place: any) => {
  if (!overlay) createOverlay();

  const content = overlay.getContent() as HTMLElement;
  content.innerHTML = `<p class="font-semibold">${place.place_name}</p>
                       <p>${place.road_address_name || place.address_name}</p>`;

  overlay.setPosition(new window.kakao.maps.LatLng(place.y, place.x));
  overlay.setMap(map.value); // 오버레이 항상 지도에 표시
};
</script>
